import { Component } from '@angular/core';
import { ToastService } from '../services/toast.service';

/**
 * Transient notifications, OVERLAID — never in the layout.
 *
 * Joel, on the screenshot confirmation: "rather than 'Chart copied to your
 * clipboard.' appearing in that right side tool bar which makes the tool bar
 * wider and pinches the charts, can you present [it] in toast style notification
 * in the upper right?"
 *
 * That is the whole contract of this component: `position: fixed` takes it out of
 * flow, so showing a message cannot move anything behind it. The chart screens
 * used to render the same text as a div INSIDE .chart-controls, which widened the
 * toolbar and reflowed the charts — the message was participating in layout,
 * which is the defect. Nothing here may be given a layout-affecting position.
 *
 * COLOURS COME FROM THE THEME, not from this file. They used to be four hardcoded
 * hexes (#2f6feb, #2e9e5b, #d98514, #d64545) that matched nothing else in the
 * app. The surface now uses the same role tokens the screenshot message already
 * used correctly — overlay background, primary text, body font — and severity is
 * carried by an accent bar in the --vc-functional-* tokens.
 *
 * The functional tokens are FOREGROUND colours (the token file documents their
 * contrast ratios against the dark background), so they are used as an accent and
 * as text, never as the panel fill. Filling a panel with a 4.6:1 foreground and
 * putting white on top is how a "themed" component ends up less readable than the
 * hardcoded one it replaced.
 */
@Component({
  selector: 'app-toast-host',
  template: `
    <div class="toast-host">
      <div *ngFor="let t of toastService.toasts$ | async" class="toast" [ngClass]="'toast-' + t.severity"
           (click)="toastService.dismiss(t.id)">
        {{ t.text }}
      </div>
    </div>
  `,
  styles: [`
    .toast-host {
      /* FIXED, and that is the requirement rather than a style choice. */
      position: fixed; top: 56px; right: 16px; z-index: 10000;
      display: flex; flex-direction: column; gap: 8px; pointer-events: none;
    }
    .toast {
      pointer-events: auto; cursor: pointer;
      min-width: 220px; max-width: 360px;
      padding: 10px 14px;
      border-radius: 4px;
      background: var(--vc-bg-overlay);
      border: 1px solid var(--vc-divider);
      border-left: 3px solid var(--vc-divider);
      color: var(--vc-text-primary);
      font-family: var(--vc-font-body);
      font-size: 12px; line-height: 1.35;
      /* The screenshot outcome names a FILE, so it has to wrap rather than clip:
         "in your downloads folder" is useless without the name. */
      overflow-wrap: anywhere;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
      animation: toast-in 160ms ease-out;
    }
    .toast-info    { border-left-color: var(--vc-functional-info); }
    .toast-success { border-left-color: var(--vc-functional-success); }
    .toast-warning { border-left-color: var(--vc-functional-warning); }
    .toast-error   { border-left-color: var(--vc-functional-error); }
    @keyframes toast-in {
      from { opacity: 0; transform: translateX(12px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .toast { animation: none; }
    }
  `],
})
export class ToastHostComponent {
  constructor(public toastService: ToastService) {}
}
